import { useQuery } from '@tanstack/react-query'
import type { ChannelPreviewProps } from 'stream-chat-react'

import { paths } from 'src/config/paths'
import { parseChatId } from 'src/features/chats/utils/parse-chat-id'
import { queries } from 'src/lib/query-keys'
import { getMatchById } from 'src/shared/api/partner-match/use-match-by-id'
import { getReferralShareById } from 'src/shared/api/referrals/use-referral-share-by-id'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { getUserById } from 'src/shared/features/users/api/accounts/use-user-by-id'
import { professionMap } from 'src/shared/features/users/schemas/profession'
import type { PublicUserData } from 'src/shared/features/users/schemas/user'
import type { ChatType } from 'src/shared/schemas/chats/chat-data'
import type { Match } from 'src/shared/schemas/partner-match'
import type { ReferralShare } from 'src/shared/schemas/referrals/referral-share'
import { formatPrintedReferralAddress } from 'src/shared/utils/format-field-value'

import { getChatType } from '../utils/get-chat-type'

interface ChatData {
  chatType?: ChatType
  title: string
  description: string
  chatPartner: PublicUserData
  referralLink?: string
  referralShare?: ReferralShare
  match?: Match
  chatPath?: string
}

const getChatData = async ({
  channel,
  type,
  myUser,
}: {
  channel: ChannelPreviewProps['channel']
  type: ChatType
  myUser: NonNullable<ReturnType<typeof useMyUserData>['data']>
}): Promise<ChatData | null> => {
  const myId = myUser.id.toString()
  const chatPartnerId = Object.keys(channel.state.members).find((id) => id !== myId)
  if (!chatPartnerId) return null

  const chatId = channel.id
  const objectId = parseChatId(channel.cid)
  if (!chatId || !objectId) return null

  const chatPartner = await getUserById(Number(chatPartnerId))
  if (!chatPartner) return null

  try {
    switch (type) {
      case 'referral': {
        const referralShare = await getReferralShareById(objectId)
        if (!referralShare) return null

        const isSender = referralShare.senderId.toString() === myId

        const title = chatPartner.fullName
        const description = formatPrintedReferralAddress(referralShare, isSender || referralShare.status === 'approved')
        const referralLink = isSender
          ? `${paths.referrals.detail.referral.getHref(referralShare.referralId.toString())}`
          : `${paths.referrals.detail.referralShare.getHref(referralShare.id.toString())}`

        const chatPath = paths.chats.conversation.referral.getHref(chatId)

        return { chatType: 'referral', referralShare, title, description, chatPartner, referralLink, chatPath }
      }
      case 'partner-match': {
        const match = await getMatchById(objectId)
        if (!match) return null

        const title = chatPartner.fullName
        const description = professionMap[chatPartner.profession].label

        const chatPath = paths.chats.conversation.match.getHref(chatId)

        return { chatType: 'partner-match', match, title, description, chatPartner, chatPath }
      }
      case 'network': {
        const title = chatPartner.fullName
        const description = professionMap[chatPartner.profession].label

        const chatPath = paths.chats.conversation.network.getHref(chatId)

        return { chatType: 'network', title, description, chatPartner, chatPath }
      }
      case 'sneak': {
        const title = chatPartner.fullName
        const description = professionMap[chatPartner.profession].label

        const chatPath = paths.chats.conversation.sneak.getHref(chatId)

        return { chatType: 'sneak', title, description, chatPartner, chatPath }
      }
      default: {
        return {
          chatType: undefined,
          title: 'Loading Chat...',
          description: '',
          chatPartner,
        }
      }
    }
  } catch (error) {
    console.error(error)
    return null
  }
}

export const useChatData = (channel: ChannelPreviewProps['channel']) => {
  const { data: myUser } = useMyUserData()
  const type = getChatType(channel)

  const chatId = channel.id || null

  return useQuery({
    queryKey: queries.chats.detail(chatId || '', type).queryKey,
    queryFn: () => {
      return getChatData({ channel, type, myUser: myUser! })
    },
    enabled: !!chatId && !!type && !!channel.data && !!myUser,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  })
}
